import { Injectable } from '@angular/core';
import { InputsService } from '@app/inputs/services/inputs.service';
import { ProfileActionOptionsConfigService } from '@app/profile/services/profile-action-options-config.service';
import { ProfileFacetsService } from '@app/profile/services/profile-facets.service';
import {
  NormalizedRecommendation,
  RecommendationForUser,
  RecommendationStatus,
  UserProfileRecommendationsModelShared,
} from '@app/recommendations/recommendations.api';
import {
  RecommendationsService,
  RecommendationType,
} from '@app/recommendations/services/recommendations.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { TagsService } from '@app/tags/services/tags.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, share, switchMap, take, tap } from 'rxjs/operators';
import { ProfileFilter } from '../profile-filter-bar/profile-filter-bar.component';
import { UserProfileShared } from './profile-shared.component';

@Injectable({
  providedIn: 'root',
})
export class ProfileSharedService extends SubscriberBaseDirective {
  public readonly recommendationType = RecommendationType.Recommendation;
  public readonly trackingArea = 'Profile Shared';
  public i18n = this.translate.instant(['Core_Type']);
  /** Call next on this to refresh profile sharedItems data */
  public refresh$ = new BehaviorSubject<UserProfileShared>(null);
  /** Subscribe to this observable to get the latest sharedItems
   * and an updated filter object
   */
  public sharedItems$ = this.refresh$.pipe(
    switchMap(() => this.loadRecommendations$)
  );
  private loadRecommendations$: Observable<{
    data: UserProfileShared;
    filter: ProfileFilter;
  }> = this.recommendationsService
    .getUserProfileRecommendations(this.recommendationType)
    .pipe(
      take(1),
      map((data) => {
        const { incomplete, complete } = this.normalizeData(data);
        // default filter
        const filter = {
          searchTerm: '',
          isActive: false,
          facets: [
            {
              id: 'Type',
              name: this.i18n.Core_Type,
              values: this.profileFacets.contentTypeFacetValues,
            },
          ],
        };

        const allData = [].concat(incomplete, complete);

        filter.facets.forEach((facet) => {
          this.profileFacets.setFacetCounts(facet, allData);
        });

        return {
          data: {
            incomplete,
            completed: complete,
          },
          filter,
        };
      }),
      share()
    );

  constructor(
    private translate: TranslateService,
    private tagsService: TagsService,
    private recommendationsService: RecommendationsService,
    private profileFacets: ProfileFacetsService,
    private inputsService: InputsService,
    private profileActionOptionsConfigService: ProfileActionOptionsConfigService
  ) {
    super();

    this.tagsService.userTagsModified
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.refresh$.next(null);
      });
  }

  public normalizeData(
    data: UserProfileRecommendationsModelShared<RecommendationForUser>
  ): UserProfileRecommendationsModelShared<NormalizedRecommendation> {
    const catergories = ['complete', 'incomplete'];
    const normalizedData = {
      complete: [],
      incomplete: [],
      statistics: data.statistics,
    };

    catergories.forEach((key) => {
      if (data[key] === undefined) {
        console.warn(`The key ${key} does not exist on ${data}`);
        return;
      }

      // get recommendations that are inputs and setup statistics
      const inputs = data[key]
        .filter((recommendation) => !!recommendation.reference.inputType)
        .map((recommendation) => recommendation.reference);

      this.inputsService.mapInputStatistics(
        inputs,
        data.statistics,
        this.mapInputStatisticsComparer
      );

      // normalize data
      normalizedData[key] =
        this.recommendationsService.normalizeRecommendations(data[key]);
    });

    return normalizedData;
  }

  public configureModifyOptionsFn(recommendation?: NormalizedRecommendation) {
    return this.profileActionOptionsConfigService.getModifyOptionsFn({
      recommendation,
      processChange: (rec, status) =>
        this.processChange(rec, status)
          .pipe(
            tap(() => {
              this.updateInput();
            })
          )
          .subscribe(),
    });
  }

  public dismiss(recommendation: NormalizedRecommendation): Observable<void> {
    return this.processChange(recommendation, 'Dismissed').pipe(
      tap(() => {
        this.refresh$.next(null);
      })
    );
  }

  private updateInput() {
    this.refresh$.next(null);
  }

  private processChange(
    recommendation: NormalizedRecommendation,
    status: RecommendationStatus
  ) {
    // Only refresh the data if the status is in this list and will cause the view to change.
    const refreshStatuses: RecommendationStatus[] = ['Dismissed', 'Complete'];
    return this.recommendationsService
      .updateRecommendationStatus({
        recommendation,
        status,
        item: recommendation.reference,
      })
      .pipe(
        take(1),
        filter(() => refreshStatuses.includes(status))
      );
  }

  private mapInputStatisticsComparer(input, stat) {
    const type = input.inputType || input.referenceType || input.contentType;
    const id = input.inputId || input.referenceId || input.contentId;

    return type === stat.inputType && id === stat.inputId;
  }
}
